import React, { useState, useEffect } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  Divider,
  Avatar,
  Chip,
  useTheme,
  alpha,
  Fade,
  Slide,
  CircularProgress
} from '@mui/material';
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Home as HomeIcon,
  Category as CategoryIcon,
  LocalOffer as LocalOfferIcon,
  Info as InfoIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  ShoppingBag as ShoppingBagIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
  FiberManualRecord as DotIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useStoreSettings } from '../../contexts/StoreSettingsContext';
import { Category, InventoryItem } from '../../utils/types';
import { getMainCategories, getSubcategories } from '../../services/categoryService';
import { getStoreProducts } from '../../services/storeService';
import { useCurrency } from '../../contexts/CurrencyContext';

const getMenuIcon = (iconName: string) => {
  const sizeStyle = { fontSize: '1.3rem' };
  switch (iconName) {
    case 'home':
      return <HomeIcon sx={sizeStyle} />;
    case 'local_offer':
    case 'offer':
      return <LocalOfferIcon sx={sizeStyle} />;
    case 'trending_up':
    case 'trending':
      return <TrendingUpIcon sx={sizeStyle} />;
    case 'star':
    case 'featured':
      return <StarIcon sx={sizeStyle} />;
    case 'info':
    case 'about':
      return <InfoIcon sx={sizeStyle} />;
    case 'phone':
    case 'contact':
      return <PhoneIcon sx={sizeStyle} />;
    case 'person':
    case 'profile':
      return <PersonIcon sx={sizeStyle} />;
    case 'category':
      return <CategoryIcon sx={sizeStyle} />;
    case 'shopping_bag':
      return <ShoppingBagIcon sx={sizeStyle} />;
    default:
      return <DotIcon sx={{ fontSize: 10 }} />;
  }
};

const defaultMainMenuItems = [
  { id: 'home', title: 'الرئيسية', icon: 'home', path: '/', enabled: true },
  { id: 'offers', title: 'العروض الخاصة', icon: 'local_offer', path: '/offers', badge: 'جديد', enabled: true },
  { id: 'bestsellers', title: 'الأكثر مبيعاً', icon: 'trending_up', path: '/bestsellers', enabled: true },
  { id: 'featured', title: 'المنتجات المميزة', icon: 'star', path: '/featured', enabled: true }
];

const defaultBottomMenuItems = [
  { id: 'about', title: 'حول المتجر', icon: 'info', path: '/about', enabled: true },
  { id: 'contact', title: 'اتصل بنا', icon: 'phone', path: '/contact', enabled: true },
  { id: 'profile', title: 'حسابي', icon: 'person', path: '/profile', enabled: true }
];

interface MobileMegaMenuProps {
  onCategorySelect: (categoryId: string) => void;
  onClose: () => void;
}

const MobileMegaMenu: React.FC<MobileMegaMenuProps> = ({
  onCategorySelect,
  onClose
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { settings } = useStoreSettings();
  const { formatCurrency } = useCurrency();

  const [mainCategories, setMainCategories] = useState<Category[]>([]);
  const [subcategoriesMap, setSubcategoriesMap] = useState<Record<string, Category[]>>({});
  const [loadingSubcategoriesFor, setLoadingSubcategoriesFor] = useState<string | null>(null);
  const [expandedCategoryId, setExpandedCategoryId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [productsMap, setProductsMap] = useState<Record<string, InventoryItem[]>>({});
  const [loadingProductsFor, setLoadingProductsFor] = useState<string | null>(null);

  // Load main categories on mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const mainCats = await getMainCategories();
        setMainCategories(mainCats.filter(c => c.isActive !== false));
      } catch (error) {
        console.error('Error loading main categories:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCategories();
  }, []);

  // Load subcategories for a given main category
  const handleToggleCategory = async (categoryId: string) => {
    if (expandedCategoryId === categoryId) {
      // Collapse
      setExpandedCategoryId(null);
      return;
    }

    setExpandedCategoryId(categoryId);

    // If subcategories not yet loaded for this category, load them
    if (!subcategoriesMap[categoryId]) {
      setLoadingSubcategoriesFor(categoryId);
      try {
        const subs = await getSubcategories(categoryId);
        const activeSubs = subs.filter(s => s.isActive !== false);
        setSubcategoriesMap(prev => ({
          ...prev,
          [categoryId]: activeSubs
        }));

        // If this category has no active subcategories, load its products
        if (activeSubs.length === 0) {
          setLoadingProductsFor(categoryId);
          try {
            const result = await getStoreProducts(categoryId, undefined, 10);
            setProductsMap(prev => ({
              ...prev,
              [categoryId]: result.products || []
            }));
          } catch (productError) {
            console.error(`Error loading products for category ${categoryId}:`, productError);
            setProductsMap(prev => ({ ...prev, [categoryId]: [] }));
          } finally {
            setLoadingProductsFor(null);
          }
        }
      } catch (error) {
        console.error(`Error loading subcategories for ${categoryId}:`, error);
        setSubcategoriesMap(prev => ({ ...prev, [categoryId]: [] }));
      } finally {
        setLoadingSubcategoriesFor(null);
      }
    } else {
      // If subcategories already loaded, check if we need to load products (subs was empty, and products not yet loaded)
      const subs = subcategoriesMap[categoryId] || [];
      if (subs.length === 0 && !productsMap[categoryId] && !loadingProductsFor) {
        setLoadingProductsFor(categoryId);
        try {
          const result = await getStoreProducts(categoryId, undefined, 10);
          setProductsMap(prev => ({
            ...prev,
            [categoryId]: result.products || []
          }));
        } catch (productError) {
          console.error(`Error loading products for category ${categoryId}:`, productError);
          setProductsMap(prev => ({ ...prev, [categoryId]: [] }));
        } finally {
          setLoadingProductsFor(null);
        }
      }
    }
  };

  type MainMenuItem = { id: string; title: string; icon: string; path: string; enabled: boolean; badge?: string; color?: string };
  type BottomMenuItem = { id: string; title: string; icon: string; path: string; enabled: boolean };

  const configuredMainMenuItems: MainMenuItem[] = settings?.display?.mobileMainMenuItems || defaultMainMenuItems;
  const configuredBottomMenuItems: BottomMenuItem[] = settings?.display?.mobileBottomMenuItems || defaultBottomMenuItems;

  const mainMenuItems = configuredMainMenuItems
    .filter(item => item.enabled !== false)
    .map(item => ({
      title: item.title,
      icon: getMenuIcon(item.icon),
      path: item.path,
      color: item.color || theme.palette.primary.main,
      badge: item.badge
    }));

  const bottomMenuItems = configuredBottomMenuItems
    .filter(item => item.enabled !== false)
    .map(item => ({
      title: item.title,
      icon: getMenuIcon(item.icon),
      path: item.path
    }));

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose();
  };

  const handleMainCategoryClick = (categoryId: string, subs: Category[]) => {
    // If no subcategories, navigate directly
    if (subs.length === 0) {
      onCategorySelect(categoryId);
      onClose();
    }
  };

  const handleSubcategoryClick = (categoryId: string) => {
    onCategorySelect(categoryId);
    onClose();
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Fade in timeout={500}>
        <Box
          sx={{
            p: 3,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: theme.palette.primary.contrastText,
            textAlign: 'center'
          }}
        >
          <Avatar
            sx={{
              width: 60,
              height: 60,
              mx: 'auto',
              mb: 2,
              border: `3px solid ${alpha(theme.palette.common.white, 0.3)}`,
              bgcolor: theme.palette.primary.light
            }}
          >
            {settings?.general?.storeName?.charAt(0) || 'م'}
          </Avatar>
          <Typography variant="h6" fontWeight="bold">
            {settings?.general?.storeName || 'اليكترونيات السواح'}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
            أفضل المنتجات بأفضل الأسعار
          </Typography>
        </Box>
      </Fade>

      {/* Main Menu */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
        <List sx={{ py: 0 }}>
          {/* Main Navigation Items */}
          {mainMenuItems.map((item, index) => (
            <Slide key={item.title} in direction="right" timeout={300 + index * 100}>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    py: 2,
                    px: 3,
                    '&:hover': {
                      bgcolor: alpha(item.color, 0.08),
                      '& .MuiListItemIcon-root': {
                        color: item.color,
                        transform: 'scale(1.1)'
                      }
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: item.color,
                      transition: 'all 0.3s ease',
                      minWidth: 48
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.title}
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontWeight: 500,
                        fontSize: '1rem'
                      }
                    }}
                  />
                  {item.badge && (
                    <Chip
                      label={item.badge}
                      size="small"
                      color="error"
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        fontWeight: 'bold'
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            </Slide>
          ))}

          <Divider sx={{ my: 1 }} />

          {/* Categories Section Header */}
          <Slide in direction="right" timeout={600}>
            <ListItem sx={{ py: 1, px: 3 }}>
              <ListItemIcon sx={{ color: theme.palette.info.main, minWidth: 48 }}>
                <CategoryIcon />
              </ListItemIcon>
              <ListItemText
                primary="الأقسام"
                sx={{
                  '& .MuiListItemText-primary': {
                    fontWeight: 700,
                    fontSize: '0.85rem',
                    color: theme.palette.text.secondary,
                    textTransform: 'uppercase',
                    letterSpacing: 1
                  }
                }}
              />
            </ListItem>
          </Slide>

          {/* Main Categories with expandable subcategories */}
          {loading ? (
            <ListItem sx={{ justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                جاري التحميل...
              </Typography>
            </ListItem>
          ) : mainCategories.length > 0 ? (
            mainCategories.map((category, index) => {
              const subs = subcategoriesMap[category.id] || [];
              const isExpanded = expandedCategoryId === category.id;
              const isLoadingSubs = loadingSubcategoriesFor === category.id;
              const hasSubsLoaded = Boolean(subcategoriesMap[category.id]);

              return (
                <Fade key={category.id} in timeout={400 + index * 60}>
                  <Box>
                    {/* Main Category Row */}
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={() => handleToggleCategory(category.id)}
                        sx={{
                          py: 1.8,
                          px: 3,
                          borderLeft: isExpanded
                            ? `4px solid ${theme.palette.primary.main}`
                            : `4px solid transparent`,
                          bgcolor: isExpanded
                            ? alpha(theme.palette.primary.main, 0.06)
                            : 'transparent',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            borderLeft: `4px solid ${theme.palette.primary.main}`,
                          },
                          transition: 'all 0.25s ease'
                        }}
                      >
                        {/* Category Image or Icon */}
                        <ListItemIcon sx={{ minWidth: 44 }}>
                          {category.imageUrl ? (
                            <Avatar
                              src={category.imageUrl}
                              sx={{ width: 32, height: 32 }}
                            />
                          ) : (
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                bgcolor: alpha(theme.palette.primary.main, 0.15),
                                color: theme.palette.primary.main,
                                fontSize: '0.9rem',
                                fontWeight: 'bold'
                              }}
                            >
                              {category.name.charAt(0)}
                            </Avatar>
                          )}
                        </ListItemIcon>

                        <ListItemText
                          primary={category.name}
                          sx={{
                            '& .MuiListItemText-primary': {
                              fontWeight: isExpanded ? 700 : 500,
                              fontSize: '0.95rem',
                              color: isExpanded ? theme.palette.primary.main : 'inherit',
                              transition: 'all 0.25s ease'
                            }
                          }}
                        />

                        {/* Loading spinner or expand icon */}
                        {isLoadingSubs ? (
                          <CircularProgress size={18} sx={{ color: theme.palette.primary.main }} />
                        ) : (
                          isExpanded ? (
                            <ExpandLessIcon
                              sx={{
                                color: theme.palette.primary.main,
                                transition: 'transform 0.3s ease'
                              }}
                            />
                          ) : (
                            <ExpandMoreIcon
                              sx={{
                                color: theme.palette.text.secondary,
                                transition: 'transform 0.3s ease'
                              }}
                            />
                          )
                        )}
                      </ListItemButton>
                    </ListItem>

                    {/* Subcategories Collapse */}
                    <Collapse in={isExpanded && hasSubsLoaded} timeout="auto" unmountOnExit>
                      <Box
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.03),
                          borderLeft: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                          ml: 3,
                          borderRadius: '0 0 8px 0'
                        }}
                      >
                        {subs.length > 0 ? (
                          <List component="div" disablePadding>
                            {/* Option to see ALL products in this main category */}
                            <ListItem disablePadding>
                              <ListItemButton
                                onClick={() => {
                                  onCategorySelect(category.id);
                                  onClose();
                                }}
                                sx={{
                                  py: 1.2,
                                  px: 2,
                                  '&:hover': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                    '& .MuiListItemText-primary': {
                                      color: theme.palette.primary.main
                                    }
                                  },
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                <ListItemIcon sx={{ minWidth: 28 }}>
                                  <DotIcon
                                    sx={{
                                      fontSize: 10,
                                      color: theme.palette.primary.main
                                    }}
                                  />
                                </ListItemIcon>
                                <ListItemText
                                  primary={`كل منتجات ${category.name}`}
                                  sx={{
                                    '& .MuiListItemText-primary': {
                                      fontSize: '0.85rem',
                                      fontWeight: 600,
                                      color: theme.palette.primary.main,
                                      fontStyle: 'italic',
                                      transition: 'color 0.2s ease'
                                    }
                                  }}
                                />
                              </ListItemButton>
                            </ListItem>
                            <Divider sx={{ mx: 2, opacity: 0.5 }} />
                            {subs.map((sub, subIndex) => (
                              <Fade key={sub.id} in timeout={200 + subIndex * 50}>
                                <ListItem disablePadding>
                                  <ListItemButton
                                    onClick={() => handleSubcategoryClick(sub.id)}
                                    sx={{
                                      py: 1.2,
                                      px: 2,
                                      '&:hover': {
                                        bgcolor: alpha(theme.palette.secondary.main, 0.1),
                                        '& .MuiListItemText-primary': {
                                          color: theme.palette.secondary.main
                                        }
                                      },
                                      transition: 'all 0.2s ease'
                                    }}
                                  >
                                    <ListItemIcon sx={{ minWidth: 28 }}>
                                      {sub.imageUrl ? (
                                        <Avatar
                                          src={sub.imageUrl}
                                          sx={{ width: 22, height: 22 }}
                                        />
                                      ) : (
                                        <DotIcon
                                          sx={{
                                            fontSize: 8,
                                            color: theme.palette.text.secondary
                                          }}
                                        />
                                      )}
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={sub.name}
                                      sx={{
                                        '& .MuiListItemText-primary': {
                                          fontSize: '0.88rem',
                                          transition: 'color 0.2s ease'
                                        }
                                      }}
                                    />
                                  </ListItemButton>
                                </ListItem>
                              </Fade>
                            ))}
                          </List>
                        ) : loadingProductsFor === category.id ? (
                          <ListItem sx={{ justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={20} />
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 1.5 }}>
                              جاري تحميل المنتجات...
                            </Typography>
                          </ListItem>
                        ) : (productsMap[category.id] || []).length > 0 ? (
                          <List component="div" disablePadding>
                            {/* Option to see ALL products in this main category */}
                            <ListItem disablePadding>
                              <ListItemButton
                                onClick={() => {
                                  onCategorySelect(category.id);
                                  onClose();
                                }}
                                sx={{
                                  py: 1.2,
                                  px: 2.5,
                                  '&:hover': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                    '& .MuiListItemText-primary': {
                                      color: theme.palette.primary.main
                                    }
                                  },
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                  <DotIcon
                                    sx={{
                                      fontSize: 10,
                                      color: theme.palette.primary.main
                                    }}
                                  />
                                </ListItemIcon>
                                <ListItemText
                                  primary="كل المنتجات"
                                  sx={{
                                    '& .MuiListItemText-primary': {
                                      fontSize: '0.85rem',
                                      fontWeight: 700,
                                      color: theme.palette.primary.main,
                                      transition: 'color 0.2s ease'
                                    }
                                  }}
                                />
                              </ListItemButton>
                            </ListItem>
                            <Divider sx={{ mx: 2.5, opacity: 0.5 }} />

                            {(productsMap[category.id] || []).slice(0, 9).map((product, pIndex) => (
                              <Fade key={product.id} in timeout={200 + pIndex * 50}>
                                <ListItem disablePadding>
                                  <ListItemButton
                                    onClick={() => {
                                      const isMobileStore = location.pathname.startsWith('/mobile-store');
                                      navigate(isMobileStore ? `/mobile-store/product/${product.id}` : `/product/${product.id}`);
                                      onClose();
                                    }}
                                    sx={{
                                      py: 1,
                                      px: 2.5,
                                      '&:hover': {
                                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                                        '& .MuiListItemText-primary': {
                                          color: theme.palette.primary.main
                                        }
                                      },
                                      transition: 'all 0.2s ease'
                                    }}
                                  >
                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                      {product.imageUrl ? (
                                        <Avatar
                                          src={product.imageUrl}
                                          variant="rounded"
                                          sx={{ width: 24, height: 24 }}
                                        />
                                      ) : (
                                        <DotIcon
                                          sx={{
                                            fontSize: 8,
                                            color: theme.palette.text.secondary
                                          }}
                                        />
                                      )}
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={product.name}
                                      secondary={formatCurrency(product.storePrice || product.sellingPrice)}
                                      sx={{
                                        '& .MuiListItemText-primary': {
                                          fontSize: '0.85rem',
                                          transition: 'color 0.2s ease',
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis'
                                        },
                                        '& .MuiListItemText-secondary': {
                                          fontSize: '0.75rem',
                                          color: theme.palette.primary.main,
                                          fontWeight: 600
                                        }
                                      }}
                                    />
                                  </ListItemButton>
                                </ListItem>
                              </Fade>
                            ))}
                            
                            {(productsMap[category.id] || []).length >= 10 && (
                              <Fade in timeout={200 + 9 * 50}>
                                <ListItem disablePadding>
                                  <ListItemButton
                                    onClick={() => {
                                      onCategorySelect(category.id);
                                      onClose();
                                    }}
                                    sx={{
                                      py: 1.2,
                                      px: 2.5,
                                      '&:hover': {
                                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                                        '& .MuiListItemText-primary': {
                                          color: theme.palette.primary.main
                                        }
                                      },
                                      transition: 'all 0.2s ease'
                                    }}
                                  >
                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                      <ShoppingBagIcon
                                        fontSize="small"
                                        sx={{ color: theme.palette.primary.main }}
                                      />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary="كل المنتجات"
                                      sx={{
                                        '& .MuiListItemText-primary': {
                                          fontSize: '0.85rem',
                                          fontWeight: 700,
                                          color: theme.palette.primary.main,
                                          transition: 'color 0.2s ease'
                                        }
                                      }}
                                    />
                                  </ListItemButton>
                                </ListItem>
                              </Fade>
                            )}
                          </List>
                        ) : (
                          <ListItem disablePadding sx={{ py: 1.5, px: 2 }}>
                            <ListItemButton
                              onClick={() => {
                                onCategorySelect(category.id);
                                onClose();
                              }}
                              sx={{
                                py: 1,
                                px: 1.5,
                                borderRadius: 1,
                                width: '100%',
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.primary.main, 0.1)
                                }
                              }}
                            >
                              <ShoppingBagIcon
                                fontSize="small"
                                sx={{ color: theme.palette.primary.main, mr: 1 }}
                              />
                              <Typography variant="body2" color="primary">
                                عرض المنتجات (لا توجد منتجات معروضة حالياً)
                              </Typography>
                            </ListItemButton>
                          </ListItem>
                        )}
                      </Box>
                    </Collapse>
                  </Box>
                </Fade>
              );
            })
          ) : (
            <ListItem sx={{ pl: 6 }}>
              <Typography variant="body2" color="text.secondary">
                لا توجد أقسام متاحة
              </Typography>
            </ListItem>
          )}

          <Divider sx={{ my: 1 }} />

          {/* Bottom Menu Items */}
          {bottomMenuItems.map((item, index) => (
            <Slide key={item.title} in direction="right" timeout={800 + index * 100}>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    py: 2,
                    px: 3,
                    '&:hover': {
                      bgcolor: alpha(theme.palette.grey[500], 0.08),
                      '& .MuiListItemIcon-root': {
                        color: theme.palette.grey[700],
                        transform: 'scale(1.1)'
                      }
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: theme.palette.grey[600],
                      transition: 'all 0.3s ease',
                      minWidth: 48
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.title}
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontWeight: 500,
                        fontSize: '1rem'
                      }
                    }}
                  />
                </ListItemButton>
              </ListItem>
            </Slide>
          ))}
        </List>
      </Box>

      {/* Footer */}
      <Fade in timeout={1000}>
        <Box
          sx={{
            p: 2,
            bgcolor: alpha(theme.palette.grey[100], 0.5),
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            textAlign: 'center'
          }}
        >
          <Typography variant="caption" color="text.secondary">
            © 2024 {settings?.general?.storeName || 'اليكترونيات السواح'}
          </Typography>
        </Box>
      </Fade>
    </Box>
  );
};

export default MobileMegaMenu;
